// MARK://dhk8/pos/pages/settings/AppearanceSettingsPage
// /settings/appearance — POS-only accent ("Color de la app") picker.
//
// Owner / manager only (gated centrally in lib/role-license-gate.ts, plus a
// belt-and-braces check here). Picking a preset (or typing a custom hex) calls
// PUT /v1/me/branding {accentColor}; on success the new accent-ramp is applied
// to :root immediately (live, no reload) via saveAccentColor() in branding-loader.
//
// The admin shells never get this screen — they stay NintaiGO-green always.

import { useState, useEffect } from 'react'
import { Palette, Check, RotateCcw } from 'lucide-react'

import { useAuthStore } from '../../stores/auth-store.js'
import { normalizeRole } from '../../lib/role-license-gate.js'
import { getBranding, saveAccentColor, applyAccentColor } from '../../lib/branding-loader.js'
import { DEFAULT_ACCENT, buildAccentRamp, applyAccentRamp } from '../../lib/accent-ramp.js'
import { t } from '../../i18n/es-MX.js'

// Preset swatches. `null` value === reset to the build-time brand default green.
const PRESETS: ReadonlyArray<{ id: string; label: string; value: string | null }> = [
  { id: 'green', label: 'Verde (predeterminado)', value: null },
  { id: 'blue', label: 'Azul', value: '#2563EB' },
  { id: 'purple', label: 'Morado', value: '#7C3AED' },
  { id: 'teal', label: 'Verde azulado', value: '#0D9488' },
  { id: 'orange', label: 'Naranja', value: '#EA580C' },
  { id: 'red', label: 'Rojo', value: '#DC2626' },
  { id: 'slate', label: 'Pizarra', value: '#475569' },
]

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const css = `
  .dhk8-appr { display:flex; flex-direction:column; gap:var(--pos-space-5,20px); max-width:560px; }
  .dhk8-appr__h1 { display:flex; align-items:center; gap:var(--pos-space-2,8px); font-size:var(--pos-text-xl,20px); font-weight:600; margin:0; color:var(--pos-fg,#E8EFE9); }
  .dhk8-appr__sub { margin:0; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-appr__grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:var(--pos-space-3,12px); }
  .dhk8-appr__swatch { display:flex; align-items:center; gap:var(--pos-space-2,8px); min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); font-size:var(--pos-text-sm,13px); cursor:pointer; transition:border-color 120ms ease, background 120ms ease; text-align:left; }
  .dhk8-appr__swatch:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-appr__swatch[aria-pressed="true"] { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 2px var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); }
  .dhk8-appr__swatch:disabled { opacity:.4; cursor:default; }
  .dhk8-appr__dot { width:18px; height:18px; border-radius:var(--pos-radius-pill,999px); flex-shrink:0; box-shadow:inset 0 0 0 1px rgba(255,255,255,.18); }
  .dhk8-appr__dot--current { margin-left:auto; }
  .dhk8-appr__custom { display:flex; align-items:flex-end; gap:var(--pos-space-3,12px); flex-wrap:wrap; }
  .dhk8-appr__custom label { display:flex; flex-direction:column; gap:var(--pos-space-1,4px); font-size:var(--pos-text-xxs,11px); text-transform:uppercase; letter-spacing:.04em; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-appr__custom input[type="text"] { min-height:44px; width:140px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); padding:0 var(--pos-space-3,12px); background:var(--pos-canvas,#0F1411); color:var(--pos-fg,#E8EFE9); font-size:var(--pos-text-sm,13px); }
  .dhk8-appr__custom input[type="color"] { min-height:44px; width:56px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0F1411); padding:4px; cursor:pointer; }
  .dhk8-appr__btn { min-height:44px; padding:0 var(--pos-space-4,16px); border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:var(--pos-on-tint,#fff); font-size:var(--pos-text-sm,13px); font-weight:500; border-radius:var(--pos-radius-md,8px); cursor:pointer; display:inline-flex; align-items:center; gap:6px; }
  .dhk8-appr__btn:hover:not(:disabled) { background:var(--pos-tint-strong,var(--hig-green-800,#0E5A2A)); }
  .dhk8-appr__btn--ghost { background:transparent; color:var(--pos-fg-secondary,#B6BFB8); border-color:var(--pos-separator-strong,rgba(255,255,255,.14)); }
  .dhk8-appr__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-appr__btn:disabled { opacity:.4; cursor:default; }
  .dhk8-appr__toast { padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-radius:var(--pos-radius-md,8px); font-size:var(--pos-text-xs,12px); }
  .dhk8-appr__toast--ok { background:var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); color:var(--pos-tint-on,var(--hig-green-300,#8BC34A)); }
  .dhk8-appr__toast--err { background:color-mix(in oklch,var(--pos-danger,#E5484D) 14%,transparent); color:var(--pos-danger,#E5484D); }
  .dhk8-appr__preview { display:flex; gap:var(--pos-space-2,8px); align-items:center; padding:var(--pos-space-3,12px); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg,12px); background:var(--pos-surface,#161C18); }
  .dhk8-appr__preview-cta { min-height:36px; padding:0 14px; border-radius:var(--pos-radius-md,8px); border:none; background:var(--pos-tint,#1F8B3A); color:var(--pos-on-tint,#fff); font-size:var(--pos-text-sm,13px); font-weight:500; }
  .dhk8-appr__preview-chip { padding:4px 10px; border-radius:var(--pos-radius-pill,999px); background:var(--pos-tint-fill,color-mix(in oklch,#1F8B3A 22%,transparent)); color:var(--pos-tint-on,var(--hig-green-300,#8BC34A)); font-size:var(--pos-text-xs,12px); }
  .dhk8-appr__forbidden { padding:var(--pos-space-4,16px); color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); }
`

export default function AppearanceSettingsPage() {
  const user = useAuthStore((s) => s.user)
  const role = normalizeRole(user?.role)
  const canEdit = role === 'gerente' || role === 'admin'

  const [current, setCurrent] = useState<string | null>(getBranding().accentColor)
  const [customHex, setCustomHex] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null)

  const flash = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ msg, kind })
    setTimeout(() => setToast(null), 2800)
  }

  // Live preview: while the page is open, mirror :root with the chosen accent.
  // We always restore the persisted accent on unmount so navigating away can't
  // leave an un-saved tint applied.
  const [preview, setPreview] = useState<string | null>(current)
  useEffect(() => {
    if (!canEdit) return
    const persisted = getBranding().accentColor
    applyAccentRamp(buildAccentRamp(preview), document.documentElement)
    return () => {
      // restore persisted value (or strip overrides when none).
      if (persisted) applyAccentColor(persisted)
      else applyAccentRamp(buildAccentRamp(null), document.documentElement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, canEdit])

  if (!canEdit) {
    return (
      <div className="dhk8-appr">
        <style>{css}</style>
        <h1 className="dhk8-appr__h1"><Palette size={18} /> {t('nav.appearance')}</h1>
        <p className="dhk8-appr__forbidden">Solo el propietario o gerente puede cambiar el color de la app.</p>
      </div>
    )
  }

  async function save(value: string | null) {
    setBusy(true)
    try {
      await saveAccentColor(value)
      setCurrent(value)
      setPreview(value)
      flash(value ? 'Color de la app actualizado' : 'Color restablecido al predeterminado')
    } catch (e) {
      flash(e instanceof Error ? e.message : 'No se pudo guardar el color', 'err')
      // On failure, snap the preview back to what's persisted.
      setPreview(getBranding().accentColor)
    } finally {
      setBusy(false)
    }
  }

  const customValid = HEX_RE.test(customHex.trim())

  return (
    <div className="dhk8-appr">
      <style>{css}</style>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 className="dhk8-appr__h1"><Palette size={18} /> {t('nav.appearance')}</h1>
        <p className="dhk8-appr__sub">El color de acento se aplica a esta terminal y a todas las del negocio. La consola de administración mantiene el verde NintaiGO.</p>
      </header>

      <section className="dhk8-appr__preview" aria-label="Vista previa">
        <button type="button" className="dhk8-appr__preview-cta" tabIndex={-1}>Acción principal</button>
        <span className="dhk8-appr__preview-chip">Pestaña activa</span>
      </section>

      <section>
        <p className="dhk8-appr__sub" style={{ marginBottom: 8 }}>Predeterminados</p>
        <div className="dhk8-appr__grid">
          {PRESETS.map((p) => {
            const selected = (p.value ?? null) === (current ?? null)
            const dot = p.value ?? DEFAULT_ACCENT
            return (
              <button
                key={p.id}
                type="button"
                className="dhk8-appr__swatch"
                aria-pressed={selected}
                disabled={busy}
                onClick={() => { setPreview(p.value ?? null); void save(p.value ?? null) }}
              >
                <span className="dhk8-appr__dot" style={{ background: dot }} />
                <span>{p.label}</span>
                {selected && <Check size={14} className="dhk8-appr__dot--current" />}
              </button>
            )
          })}
        </div>
      </section>

      <section className="dhk8-appr__custom">
        <label>
          Color personalizado
          <input
            type="text"
            placeholder="#1F8B3A"
            value={customHex}
            onChange={(e) => setCustomHex(e.target.value)}
            onBlur={() => { if (customValid) setPreview(customHex.trim()) }}
          />
        </label>
        <input
          type="color"
          aria-label="Selector de color"
          value={customValid ? customHex.trim() : (preview ?? DEFAULT_ACCENT)}
          onChange={(e) => { setCustomHex(e.target.value); setPreview(e.target.value) }}
        />
        <button
          type="button"
          className="dhk8-appr__btn"
          disabled={busy || !customValid}
          onClick={() => void save(customHex.trim())}
        >
          <Check size={14} /> Aplicar color
        </button>
        <button
          type="button"
          className="dhk8-appr__btn dhk8-appr__btn--ghost"
          disabled={busy || (current === null)}
          onClick={() => { setCustomHex(''); setPreview(null); void save(null) }}
        >
          <RotateCcw size={14} /> Restablecer
        </button>
      </section>

      {toast && <div className={`dhk8-appr__toast dhk8-appr__toast--${toast.kind}`}>{toast.msg}</div>}
    </div>
  )
}
